// src/components/layout/index.jsx
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import Header from "./Header";
import Sidebar from "../navbar"; // left menu
import {
  Copyright,
  Facebook,
  X,
  Linkdin,
  Instagram,
  Youtube,
} from "../../images";
import {
  CONTRACT_ACTION,
  INVOICE_ACTION,
  MASTER_ACTION,
  TIMESHEET_ACTION,
  DOCUMENT_ACTION,
} from "../../redux/action";
import {
  Toaster,
  getRolePath,
  getUserRole,
} from "../../utils/common";
import SessionTimeout from "../../utils/sessionTimeout";
import { startTabHeartbeat, setTabSession } from "../../utils/tabHeartbeat";
import { useNotificationFeed } from "../../hooks/useNotificationFeed";
import {
  getDocumentNotificationRoute,
  getMessageNotificationRoute,
  isDocumentOrFolderNotification,
  isMessageNotification,
  collectNewMessageNotifications,
  showMessageNotificationToast,
  seedMessageNotificationToasts,
  clearMessageNotificationToastState,
} from "../../utils/notificationNavigation";
import {
  enrichNotificationFromDocumentDetail,
  resolveClientDocumentNotification,
  showDocumentUnavailableMessage,
} from "../../utils/documentAvailability";
import {
  clearOpenClientDetailsId,
  enforceClientDetailsRouteGuard,
  extractClientDetailsIdFromPath,
  getClientDetailsHeartbeatIntervalMs,
  getClientDetailsReconcileIntervalMs,
  handleClientDetailsStorageSync,
  isClientDetailsStorageEventKey,
  navigateWithClientDetailsGuard,
  reconcileClientDetailsRoute,
  touchClientDetailsHeartbeat,
} from "../../utils/clientDetailsSession";
import PhoneNumberModal from "../PhoneNumberModal";
import { EMPLOYEE_ACTION, CLIENT_ACTION, MESSAGE_ACTION } from "../../redux/action";
import { useStaffNotificationFilter } from "../../hooks/useStaffNotificationFilter";
import { ClientCategoryProvider } from "../../context/ClientCategoryContext";
import TourGuide from "../TourGuide";
import {
  isLoginStaffUser,
  applyPhoneToLoginUser,
  updateLoginPhoneFor2FA,
  resolveRequired2faPhoneFromApi,
  buildUserDataFromApiProfile,
  hasPhone2faPromptBeenShown,
  markPhone2faPromptShown,
  clearPhone2faPromptSession,
} from "../../utils/loginPhone2fa";
import {
  OTP_CHANNEL_EMAIL,
  OTP_CHANNEL_SMS,
  fetchTwoFactorAuthPreference,
  updateTwoFactorAuthPreference,
} from "../../utils/twoFactorAuthPreference";

import "./layout.css";

const Layout = () => {
  const { visibleNotifications } = useStaffNotificationFilter();
  const { loadMore, markReadLocal, hasMore, loadingMore, unreadCount } =
    useNotificationFeed();
  const USER_TYPE = getUserRole();
  const firstName = localStorage.getItem("first_name") || "";
  const lastName = localStorage.getItem("last_name") || "";
  const fullName = `${firstName} ${lastName}`.trim() || "User";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);

  // Tour Guide automatic redirect for Client from Dashboard to Service Offers
  useEffect(() => {
    if (USER_TYPE === "client") {
      // Check if they are a new client (registered on/after July 1, 2026)
      // const userDataStr = localStorage.getItem("USER_DATA");
      // let isNewClient = false;
      // if (userDataStr) {
      //   try {
      //     const userData = JSON.parse(userDataStr);
      //     if (userData && userData.createdAt) {
      //       const createDate = new Date(userData.createdAt);
      //       const cutoffDate = new Date("2026-07-01T00:00:00.000Z");
      //       isNewClient = createDate >= cutoffDate;
      //     }
      //   } catch (e) {
      //     console.error(e);
      //   }
      // }
      // if (!isNewClient) return;

      const getIsOnboardingCompleted = () => {
        try {
          const userDataStr = localStorage.getItem("USER_DATA");
          if (!userDataStr) return false;
          const userData = JSON.parse(userDataStr);
          const flag = userData?.service_onboarding_completed ?? userData?.serviceOnboardingCompleted;
          return flag === true || flag === 1 || String(flag).toLowerCase() === "true";
        } catch {
          return false;
        }
      };
      const isOnboardingCompleted = getIsOnboardingCompleted();

      if (!isOnboardingCompleted) {
        if (location.pathname === getRolePath("/dashboard") || location.pathname === `/finlo/${USER_TYPE}`) {
          navigate(getRolePath("/my_services"), { replace: true });
        }
      }
    }
  }, [location.pathname, USER_TYPE, navigate]);
  const mainRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 1024) return true;
    return getUserRole() !== "client";
  });
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [userData, setUserData] = useState(null);

  const collapsed = sidebarCollapsed;

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const handleScroll = () => {
    const main = mainRef.current;
    if (!main) return;
    const isBottom = main.scrollHeight - main.scrollTop <= main.clientHeight + 15;
    setIsAtBottom(isBottom);
  };

  useEffect(() => {
    const main = mainRef.current;
    if (main) {
      const checkBottom = () => {
        const isBottom = main.scrollHeight - main.scrollTop <= main.clientHeight + 15;
        setIsAtBottom(isBottom);
      };
      checkBottom();
      setTimeout(checkBottom, 100);
      setTimeout(checkBottom, 300);
    }
  }, [location.pathname, sidebarCollapsed]);

  // After login: prompt once per session if dedicated 2FA phone is missing.
  useEffect(() => {
    if (hasPhone2faPromptBeenShown()) {
      return;
    }

    const userDataStr = localStorage.getItem("USER_DATA");
    if (!userDataStr) return;

    let cancelled = false;
    let timer = null;

    (async () => {
      try {
        const userDataObj = JSON.parse(userDataStr);
        const staff = isLoginStaffUser(userDataObj);
        const userId = staff
          ? userDataObj.employee_id
          : userDataObj.client_id;

        if (!userId) return;

        const [freshProfile, pref] = await Promise.all([
          staff
            ? dispatch(EMPLOYEE_ACTION.getEmployee(userId)).unwrap()
            : dispatch(CLIENT_ACTION.getClientById(userId)).unwrap(),
          fetchTwoFactorAuthPreference(),
        ]);

        if (cancelled || hasPhone2faPromptBeenShown()) return;

        const apiProfile =
          freshProfile &&
          typeof freshProfile === "object" &&
          !Array.isArray(freshProfile)
            ? freshProfile
            : {};

        const apiBackedUser = buildUserDataFromApiProfile(
          userDataObj,
          apiProfile,
          pref || {},
        );

        const requiredPhone = resolveRequired2faPhoneFromApi(
          apiProfile,
          pref || {},
          staff,
        );

        // If dedicated 2FA phone already exists, no prompt needed.
        if (requiredPhone && String(requiredPhone).trim() !== "") {
          return;
        }

        setUserData(apiBackedUser);
        timer = setTimeout(() => {
          if (!cancelled && !hasPhone2faPromptBeenShown()) {
            markPhone2faPromptShown();
            setShowPhoneModal(true);
          }
        }, 400);
      } catch (error) {
        console.error("Error checking 2FA phone:", error);
      }
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [dispatch]);

  useEffect(() => {
    if (showPhoneModal) {
      markPhone2faPromptShown();
    }
  }, [showPhoneModal]);

  useEffect(() => {
    ["role", "source", "country", "role", "payment_mode", "activity"].forEach(
      (name) => {
        dispatch(MASTER_ACTION.getMasterData({ name }));
      },
    );
    dispatch(MASTER_ACTION.getSubActivityDetails());
    if (localStorage.getItem("user_id")) {
      dispatch(MESSAGE_ACTION.getUnreadMessageSummary());
    }
  }, [dispatch]);

  const logout = () => {
    dispatch({ type: "resetReducers" });
    clearMessageNotificationToastState();
    clearOpenClientDetailsId();
    clearPhone2faPromptSession();
    localStorage.clear();
    navigate("/");
  };

  // Staff: only one client details page at a time (shared across tabs via localStorage).
  useLayoutEffect(() => {
    enforceClientDetailsRouteGuard(
      navigate,
      location.pathname,
      USER_TYPE,
      prevPathnameRef.current,
    );
    prevPathnameRef.current = location.pathname;
  }, [location.pathname, navigate, USER_TYPE]);

  useEffect(() => {
    if (USER_TYPE === "client") return undefined;

    const clientId = extractClientDetailsIdFromPath(location.pathname);
    if (!clientId) return undefined;

    const heartbeatId = setInterval(() => {
      reconcileClientDetailsRoute(navigate, location.pathname, USER_TYPE);
    }, getClientDetailsReconcileIntervalMs());

    const keepAliveId = setInterval(() => {
      reconcileClientDetailsRoute(navigate, location.pathname, USER_TYPE);
      touchClientDetailsHeartbeat();
    }, getClientDetailsHeartbeatIntervalMs());

    return () => {
      clearInterval(heartbeatId);
      clearInterval(keepAliveId);
    };
  }, [location.pathname, navigate, USER_TYPE]);

  useEffect(() => {
    if (USER_TYPE === "client") return undefined;

    const onStorage = (event) => {
      if (!isClientDetailsStorageEventKey(event.key)) return;
      handleClientDetailsStorageSync(navigate, location.pathname, USER_TYPE);
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [location.pathname, navigate, USER_TYPE]);

  // Session timeout management
  const sessionTimeoutRef = useRef(null);
  const onUpdateNotificationRef = useRef(null);

  useEffect(() => {
    // Mark this tab as having the app open (so we don't log out when other tabs close)
    if (localStorage.getItem("user_id")) {
      setTabSession();
    }
  }, []);

  useEffect(() => {
    // Initialize session timeout and tab heartbeat when component mounts (user logged in)
    if (localStorage.getItem("user_id")) {
      sessionTimeoutRef.current = new SessionTimeout(() => {
        // Show a message before logging out
        Toaster.warning(
          "Your session has expired due to inactivity. Please login again.",
        );
        logout();
      });
      sessionTimeoutRef.current.init();
    }

    const stopTabHeartbeat = localStorage.getItem("user_id")
      ? startTabHeartbeat()
      : () => {};

    // Cleanup on unmount
    return () => {
      stopTabHeartbeat();
      if (sessionTimeoutRef.current) {
        sessionTimeoutRef.current.destroy();
        sessionTimeoutRef.current = null;
      }
    };
  }, []); // Only run once on mount

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) {
        setSidebarCollapsed(true);
      } else if (getUserRole() === "client") {
        setSidebarCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onUpdateNotification = async (notify_data) => {
    const ACTION = {
      folder: [null, getRolePath("/documents")],
      document: [null, getRolePath("/documents")],
      invoice: [
        INVOICE_ACTION.getInvoiceDetail,
        USER_TYPE === "client"
          ? getRolePath("/payment")
          : getRolePath("/invoice_contract"),
      ],
      contract: [
        CONTRACT_ACTION.getContractDetail,
        USER_TYPE === "client"
          ? getRolePath("/contract_proposal")
          : getRolePath("/invoice_contract"),
      ],
      timesheet: [
        TIMESHEET_ACTION.getTimesheetEntries,
        getRolePath("/timesheet_dashboard"),
      ],
    };

    const { notification_type, notification_item_id } = notify_data;

    if (isMessageNotification(notify_data)) {
      const route = getMessageNotificationRoute(notify_data, USER_TYPE);
      if (!route) {
        Toaster.error("Could not open message thread");
        return;
      }
      navigateWithClientDetailsGuard(navigate, route.path, {
        state: route.state,
      });
      try {
        await MASTER_ACTION.updateNotification(notify_data.notification_id);
        markReadLocal(notify_data.notification_id);
      } catch {
        /* mark-read is best-effort; do not block navigation */
      }
      return;
    }

    await MASTER_ACTION.updateNotification(notify_data.notification_id);
    markReadLocal(notify_data.notification_id);

    // Handle timesheet notifications differently - navigate directly with timesheet_id
    if (notification_type === "timesheet") {
      navigate(ACTION.timesheet[1], {
        state: {
          timesheet_id: notification_item_id,
          fromNotification: true,
        },
      });
      return;
    }

    if (isDocumentOrFolderNotification(notify_data)) {
      let enriched = notify_data;

      if (USER_TYPE === "client" && notification_type === "document") {
        const resolved = await resolveClientDocumentNotification(notify_data);
        if (!resolved.ok) {
          showDocumentUnavailableMessage();
          return;
        }
        enriched = resolved.enriched;
      } else if (
        !notify_data.document_category &&
        !notify_data.documentCategory &&
        notification_item_id
      ) {
        try {
          const detailRes =
            notification_type === "document"
              ? await DOCUMENT_ACTION.getDocumentDetails(notification_item_id)
              : await DOCUMENT_ACTION.getFolderDetails(notification_item_id);
          if (detailRes?.status === 200 && detailRes?.data?.data) {
            enriched = enrichNotificationFromDocumentDetail(
              notify_data,
              detailRes,
            );
          }
        } catch {
          /* use notification payload as-is */
        }
      }

      const route = getDocumentNotificationRoute(enriched, USER_TYPE);
      if (!route) {
        Toaster.error("Could not open documents for this client");
        return;
      }
      navigateWithClientDetailsGuard(navigate, route.path, {
        state: route.state,
      });
      return;
    }

    if (notification_type === "leave") {
      navigate(getRolePath("/dashboard"), {
        state: {
          openLeavePopup: true,
          leave_id: notification_item_id,
        },
      });
      return;
    }

    // Check if ACTION exists for this notification type
    if (!ACTION[notification_type]) {
      Toaster.error("Unknown notification type");
      return;
    }

    const response = await ACTION[notification_type][0](notification_item_id);
    if (response.status === 200) {
      navigate(ACTION[notification_type][1], {
        state: {
          client_id: response.data.data.billed_to,
          mode: notification_type,
          data: response.data.data,
          view: true,
        },
      });
    } else {
      Toaster.error("Error occurred while directing to the path");
    }
  };

  onUpdateNotificationRef.current = onUpdateNotification;

  // Show message toasts when notifications refresh (page change / reload via PrivateRoute).
  useEffect(() => {
    const list = Array.isArray(visibleNotifications)
      ? visibleNotifications
      : [];

    if (seedMessageNotificationToasts(list)) {
      return;
    }

    const fresh = collectNewMessageNotifications(list);

    fresh.forEach((item) => {
      showMessageNotificationToast(item, (notifyData) => {
        onUpdateNotificationRef.current?.(notifyData);
      });
    });
  }, [visibleNotifications]);

  const notificationsLength = useMemo(() => {
    if (unreadCount != null && Number.isFinite(Number(unreadCount))) {
      return Number(unreadCount);
    }
    return visibleNotifications.filter((d) => d.view_status === "Not Viewed")
      .length;
  }, [unreadCount, visibleNotifications]);

  const persistSavedPhoneLocally = (phoneNumber) => {
    const userDataLocal = JSON.parse(localStorage.getItem("USER_DATA") || "{}");
    const nextUser = applyPhoneToLoginUser(userDataLocal, phoneNumber);
    nextUser.otp_channel = OTP_CHANNEL_SMS;
    nextUser.otpChannel = OTP_CHANNEL_SMS;
    nextUser.two_factor_channel = OTP_CHANNEL_SMS;
    localStorage.setItem("USER_DATA", JSON.stringify(nextUser));
    setUserData(nextUser);
    return nextUser;
  };

  // Handle 2FA phone update (staff personal_phone / client mfa_phone)
  const handlePhoneNumberUpdate = async (phoneNumber) => {
    const current =
      userData || JSON.parse(localStorage.getItem("USER_DATA") || "{}");
    if (!current || (!current.client_id && !current.employee_id)) {
      Toaster.error("User data not found");
      return;
    }

    try {
      const staff = isLoginStaffUser(current);
      const userId = staff ? current.employee_id : current.client_id;

      // Update 2FA preference to SMS and save verified phone number
      const prefResult = await updateTwoFactorAuthPreference({
        otpChannel: OTP_CHANNEL_SMS,
        phone: phoneNumber,
        userData: current,
      });

      if (prefResult.success) {
        const nextUser =
          prefResult.userData || persistSavedPhoneLocally(phoneNumber);
        setUserData(nextUser);
        Toaster.success(
          staff
            ? "Personal phone updated and 2-Step Verification enabled"
            : "MFA phone number updated and 2-Step Verification enabled",
        );
        return;
      }

      // If update-2fa-preference endpoint failed, try direct update with otp_channel
      if (!staff) {
        const response = await CLIENT_ACTION.insertUpdateClient({
          params: {
            mfa_phone: phoneNumber,
            otp_channel: OTP_CHANNEL_SMS,
          },
          insertUpdateFlag: 1,
          client_id: userId,
        });

        if (response?.status === 200 && response?.data?.status === 1) {
          persistSavedPhoneLocally(phoneNumber);
          Toaster.success(
            "MFA phone number updated and 2-Step Verification enabled",
          );
          return;
        }

        const fallback = await updateLoginPhoneFor2FA({
          email: current.email,
          phone: phoneNumber,
          loginUser: current,
        });
        if (fallback.success) {
          persistSavedPhoneLocally(phoneNumber);
          Toaster.success(
            "MFA phone number updated and 2-Step Verification enabled",
          );
          return;
        }

        Toaster.error(
          response?.data?.message ||
            fallback.message ||
            prefResult.message ||
            "Failed to update MFA phone number",
        );
        throw new Error("Failed to update MFA phone number");
      }

      const response = await EMPLOYEE_ACTION.insertUpdateEmployee({
        params: {
          personal_phone: phoneNumber,
          otp_channel: OTP_CHANNEL_SMS,
        },
        insertUpdateFlag: 1,
        employee_id: userId,
      });

      if (response?.status === 200 && response?.data?.status === 1) {
        persistSavedPhoneLocally(phoneNumber);
        Toaster.success(
          "Personal phone updated and 2-Step Verification enabled",
        );
        return;
      }

      const fallback = await updateLoginPhoneFor2FA({
        email: current.email,
        phone: phoneNumber,
        loginUser: current,
      });
      if (fallback.success) {
        persistSavedPhoneLocally(phoneNumber);
        Toaster.success(
          "Personal phone updated and 2-Step Verification enabled",
        );
        return;
      }

      Toaster.error(
        response?.data?.message ||
          fallback.message ||
          prefResult.message ||
          "Failed to update personal phone",
      );
      throw new Error("Failed to update personal phone");
    } catch (error) {
      console.error("Error updating 2FA phone number:", error);
      if (!error?.message?.includes("Failed to update")) {
        Toaster.error("Failed to update phone number. Please try again.");
      }
      throw error;
    }
  };

  // Optional: simple title based on route – defaults to "Dashboard"
  const currentTitle = useMemo(() => {
    const role = getUserRole().toLowerCase();
    if (location.pathname.includes(`/${role}/client_transfer`))
      return "Client Transfer";
    if (location.pathname.includes(`/${role}/new_client_requests`))
      return "New Clients Request";
    if (location.pathname.includes(`/${role}/pending_services`))
      return "Services";
    if (
      location.pathname.includes(`/${role}/client`) ||
      location.pathname.includes("/client_details")
    )
      return "Client";
    if (location.pathname.includes(`/${role}/documents`)) return "Documents";
    if (location.pathname.includes(`/${role}/invoice`)) return "Invoice";
    if (location.pathname.includes(`/${role}/employees`)) return "Employees";
    if (location.pathname.includes(`/${role}/profile`)) return "Profile";
    if (location.pathname.includes(`/${role}/my_tax_team`))
      return "My Professionals";
    if (location.pathname.includes(`/${role}/tax_strategy`))
      return "Tax Planning";
    if (location.pathname.includes(`/${role}/messages`)) return "Messages";
    if (location.pathname.includes(`/${role}/payment`)) return "Payments";
    if (location.pathname.includes(`/${role}/feedback`)) return "Feedback";
    if (location.pathname.includes(`/${role}/businesses`)) return "Businesses";
    if (location.pathname.includes(`/${role}/reports`)) return "Reports";
    if (location.pathname.includes(`/${role}/notifications`))
      return "Notifications";
    if (location.pathname.includes(`/${role}/performance_index`))
      return "Employee Performance Index";
    if (location.pathname.includes(`/${role}/timesheet_dashboard`))
      return "Timesheet";
    if (location.pathname.includes(`/${role}/create_timesheet`))
      return "Timesheet";
    if (location.pathname.includes(`/${role}/manage_client_role`))
      return "Manage Client Role";
    if (location.pathname.includes(`/${role}/contract_proposal`))
      return "Contract Proposal";
    if (location.pathname.includes(`/${role}/referral`))
      return "Referral Program";
    if (
      location.pathname.includes(`/${role}/service_offers`) ||
      location.pathname.includes(`/${role}/our_services`)
    )
      return "Our Service";
    if (location.pathname.includes(`/${role}/contact_us`)) return "Help & Support";
    if (location.pathname.includes(`/${role}/change_password`))
      return "Change Password";
    if (location.pathname.includes(`/${role}/dashboard`)) return "Dashboard";
    if (location.pathname.includes(`/${role}/leaves`)) return "Leaves";
    if (location.pathname.includes(`/${role}/efiling`)) return "E-filing";
    if (location.pathname.includes(`/${role}/templates`)) return "Templates";
    if (location.pathname.includes(`/${role}/hrms`)) return "HRMS";
  }, [location.pathname]);

  return (
    <ClientCategoryProvider>
      <div className={`finlo-shell ${collapsed ? "is-collapsed" : ""}`}>
        {/* Top header bar */}
        <Header
          title={currentTitle}
          userName={fullName}
          notifications={visibleNotifications}
          notificationsCount={notificationsLength}
          collapsed={collapsed}
          onHamburgerClick={toggleSidebar}
          onNotificationMenuClick={onUpdateNotification}
          onNotificationLoadMore={loadMore}
          notificationHasMore={hasMore}
          notificationLoadingMore={loadingMore}
          onAccountDetails={() => navigate(getRolePath("/profile"))}
          onChangePassword={() => navigate(getRolePath("/change_password"))}
          onLogout={logout}
        />

        {/* Left sidebar (your existing navbar), positioned under the header */}
        <div className="side-nav">
          <Sidebar collapsed={collapsed} onHamburgerClick={toggleSidebar} />
        </div>

        {/* Main content area to the right of sidebar, below header */}
        <div className="main-area">
          <main ref={mainRef} onScroll={handleScroll}>
            <Outlet />

            {/* Spacer so that scroll to bottom has space for the fixed footer */}
            <div className="footer-scroll-spacer" />

            <div
              className={`layout-footer ${USER_TYPE === "client" ? "client-footer" : ""} ${
                isAtBottom ? "is-at-bottom" : ""
              }`}
            >
              <div className="layout-copyright">
                <span>Ⓒ FinloTax Inc. 2026</span>{" "}
                <span className="policy-links">
                  |{" "}
                  <a
                    href="https://finlotax.com/privacy-policy/"
                    target="_blank"
                  >
                    Privacy Policy
                  </a>
                </span>
              </div>
              {USER_TYPE === "client" && (
                <div className="footer-media-links">
                  <a
                    href="https://www.facebook.com/finlotaxinc"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={Facebook} alt="facebook" />
                  </a>
                  <a
                    href="https://x.com/FinloTax"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={X} alt="X" />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/finlotax-inc/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={Linkdin} alt="Linkdin" />
                  </a>
                  <a
                    href="https://www.instagram.com/finlotax_inc/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={Instagram} alt="Instagram" />
                  </a>
                  <a
                    href="https://www.youtube.com/channel/UCjO5Yf5eGs1600kIZCgOT-Q"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={Youtube} alt="Youtube" />
                  </a>
                </div>
              )}
            </div>
          </main>

          {/* <div className="footer-wrapper">
          <img src={Copyright} alt="" />
          <div>
            {`FinloTax Inc, ${new Date().getFullYear()}  `}
            <span>
              &nbsp;&nbsp;&nbsp;Powered by{" "}
              <a href="https://www.trymool.ai" target="blank">
                MoolAI
              </a>
            </span>
          </div>
        </div> */}
        </div>

        {/* Phone Number Modal — after login, once per session */}
        <PhoneNumberModal
          open={showPhoneModal}
          userEmail={userData?.email || localStorage.getItem("email") || ""}
          title={
            isLoginStaffUser(userData)
              ? "Personal phone required"
              : "MFA phone number required"
          }
          message={
            isLoginStaffUser(userData)
              ? "Please add your personal phone for 2FA (two factor authentication)."
              : "Please add your MFA phone number for 2FA (two factor authentication)."
          }
          labelname={
            isLoginStaffUser(userData) ? "Personal phone" : "MFA phone number"
          }
          saveLabel="Verify & Save"
          onClose={() => {
            markPhone2faPromptShown();
            setShowPhoneModal(false);
          }}
          onSave={handlePhoneNumberUpdate}
        />
      </div>
      <TourGuide />
    </ClientCategoryProvider>
  );
};

export default Layout;
